import { useState, useRef, useCallback, useEffect } from "react";
import { Dumbbell, Pause, Rocket, Wind, Flame } from "lucide-react";
import { useActiveSession } from "@/hooks/useActiveSession";
import { cn } from "@/lib/utils";

function detectSessionType(title: string) {
  const t = title.toLowerCase();
  if (t.includes("explosiv") || t.includes("plio") || t.includes("puissance") || t.includes("vitesse")) return "explosivite";
  if (t.includes("force") || t.includes("hypertrophie") || t.includes("muscul")) return "force";
  if (t.includes("endurance") || t.includes("cardio") || t.includes("aérobie") || t.includes("course") || t.includes("running")) return "endurance";
  return "default";
}

const iconConfig = {
  explosivite: { icon: Rocket, bg: "bg-amber-500", text: "text-amber-950" },
  force: { icon: Dumbbell, bg: "bg-red-500", text: "text-red-950" },
  endurance: { icon: Wind, bg: "bg-sky-500", text: "text-sky-950" },
  default: { icon: Flame, bg: "bg-primary", text: "text-primary-foreground" },
} as const;

const STORAGE_KEY = "session-bubble-pos";

function loadPosition(): { x: number; y: number } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { x: window.innerWidth - 140, y: window.innerHeight - 80 };
}

export default function ActiveSessionMiniBanner() {
  const { session, isOpen, openOverlay, steps, startedAt } = useActiveSession();
  const [pos, setPos] = useState(loadPosition);
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const moved = useRef(false);
  const [elapsed, setElapsed] = useState(0);

  // Timer for bubble
  useEffect(() => {
    if (!startedAt || isOpen || !session) return;
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startedAt, isOpen, session]);

  // Clamp on resize
  useEffect(() => {
    const handler = () => {
      setPos((p) => ({
        x: Math.min(p.x, window.innerWidth - 56),
        y: Math.min(p.y, window.innerHeight - 56),
      }));
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    dragging.current = true;
    moved.current = false;
    offset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [pos]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    moved.current = true;
    const newX = Math.max(0, Math.min(window.innerWidth - 56, e.clientX - offset.current.x));
    const newY = Math.max(0, Math.min(window.innerHeight - 56, e.clientY - offset.current.y));
    setPos({ x: newX, y: newY });
  }, []);

  const handlePointerUp = useCallback(() => {
    dragging.current = false;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    if (!moved.current) {
      openOverlay();
    }
  }, [pos, openOverlay]);

  if (!session || isOpen) return null;

  const sType = detectSessionType(session.title);
  const sConf = iconConfig[sType];
  const SessionIcon = sConf.icon;
  const doneCount = steps.filter((s) => s.done).length;
  const total = steps.length;
  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div
      className="fixed z-50 flex items-center gap-1"
      style={{ left: pos.x, top: pos.y, touchAction: "none" }}
    >
      <button
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className={cn(
          "relative flex h-14 w-14 items-center justify-center rounded-full shadow-lg hover:shadow-xl transition-shadow cursor-grab active:cursor-grabbing",
          sConf.bg, sConf.text
        )}
      >
        <SessionIcon className="h-6 w-6" />
        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full bg-card border border-border px-1.5 py-0.5 text-[9px] font-medium text-foreground shadow-sm whitespace-nowrap">
          {formatTime(elapsed)} · {doneCount}/{total}
        </span>
        {/* Pause indicator */}
        <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-muted border border-border">
          <Pause className="h-2 w-2 text-muted-foreground fill-muted-foreground" />
        </span>
      </button>
    </div>
  );
}
